<template>
    <div class="container-fluid">
        <h1>{{ title }}</h1>
        <br />
        <b-form
            ref="profileform"
            name="profileForm"
            v-on:submit.prevent="$_onSubmit"
            class="pb-5"
        >
            <div class="col-xs-8 col-md-5">
                <!-- Name -->
                <TextInput
                    :text="$t('labelProfileName')"
                    v-model="record.name"
                    :name="'name'"
                    :state="!errorInUse"
                    @input="$_exists"
                    :invalidMessage="$t('profileInUse')"
                    required
                    :maxlength="50"
                    autofocus
                >
                </TextInput>
                <div v-if="isProfileThemeFeedback || isProfileApi">
                    <x-checkbox
                        :id="'IsDefault'"
                        :name="'IsDefault'"
                        v-model="record.isDefault"
                        :text="$t('isdefaultprofile')"
                    >
                    </x-checkbox>
                    <br />
                </div>
            </div>
            <div :class="[isProfileApi ? 'col-md-5' : 'col-md-8', 'col-xs-8']">
                <!-- Theme select deselect block with ordering -->
                <div v-if="isProfileThemeFeedback">
                    <DualListBox
                        class="pb-4"
                        :titleAvailable="$t('labelThemesAvailable')"
                        :titleSelected="$t('labelThemesSelected')"
                        :itemsAvailable="computedAvailableThemes"
                        :itemsSelected="computedSelectedThemes"
                        :items="computedThemes"
                        :itemDisplayName="'name'"
                        :showUpDownButtons="false"
                        @update="$_updateComputedThemes($event)"
                    >
                    </DualListBox>
                    <DualListBox
                        class="pb-4"
                        :titleAvailable="$t('labelTableSearchResultsAvailable')"
                        :titleSelected="$t('labelTableSearchResultsSelected')"
                        :itemsAvailable="computedAvailableTableSearchResults"
                        :itemsSelected="computedSelectedTableSearchResults"
                        :items="computedTableSearchResults"
                        :itemDisplayName="'name'"
                        @update="$_updateComputedTableSearchResults($event)"
                    >
                    </DualListBox>
                    <DualListBox
                        v-if="getCheckForFeatureToggle('60002')"
                        :titleAvailable="$t('layers3D.labelLayers3DAvailable')"
                        :titleSelected="$t('layers3D.labelLayers3DSelected')"
                        :itemsAvailable="computedAvailableLayers3D"
                        :itemsSelected="computedSelectedLayers3D"
                        :items="computedLayers3D"
                        :itemDisplayName="'name'"
                        @update="$_updateComputedLayers3D($event)"
                    >
                    </DualListBox>
                </div>
                <!-- Baselayer select deselect block with ordering -->
                <div v-if="isProfileBaseLayer">
                    <DualListBox
                        :titleAvailable="$t('labelSubstrateLayers')"
                        :titleSelected="$t('labelSubstrateLayersSelected')"
                        :itemsAvailable="computedAvailableBaseLayers"
                        :itemsSelected="computedSelectedBaseLayers"
                        :items="computedBaseLayers"
                        :itemDisplayName="'title'"
                        @update="$_updateComputedBaseLayers($event)"
                    >
                    </DualListBox>
                </div>
                <br />
                <!-- Storage layers rights -->
                <div v-if="isProfileThemeFeedback">
                    <!-- Measure -->
                    <h4>{{ $t('labelRightsMeasure') }}</h4>
                    <b-table
                        striped
                        hover
                        small
                        outlined
                        bordered
                        :tbody-tr-class="$_rowClass"
                        :items="computedThemesMeasure"
                        :fields="fields"
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="sortDesc"
                    >
                        <template v-slot:cell(selected)="row">
                            <b-form-checkbox
                                v-bind:id="`${row.item.id}selected`"
                                v-bind:name="`${row.item.id}selected`"
                                v-model="row.item.selected"
                                :disabled="row.item.edit"
                            >
                            </b-form-checkbox>
                        </template>
                        <template v-slot:cell(edit)="row">
                            <b-form-checkbox
                                v-bind:id="`${row.item.id}edit`"
                                v-bind:name="`${row.item.id}edit`"
                                v-model="row.item.edit"
                                @input="$_onInputEdit(row.item)"
                            >
                            </b-form-checkbox>
                        </template>
                        <template v-slot:cell(remove)="row">
                            <b-form-checkbox
                                v-bind:id="`${row.item.id}remove`"
                                v-bind:name="`${row.item.id}remove`"
                                v-model="row.item.remove"
                                :disabled="!row.item.edit"
                            >
                            </b-form-checkbox>
                        </template>
                    </b-table>
                    <!-- Feedback -->
                    <h4>{{ $t('labelRightsFeedback') }}</h4>
                    <b-table
                        striped
                        hover
                        small
                        outlined
                        bordered
                        :tbody-tr-class="$_rowClass"
                        :items="computedThemesFeedback"
                        :fields="fields"
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="sortDesc"
                    >
                        <template v-slot:cell(selected)="row">
                            <b-form-checkbox
                                v-bind:id="`${row.item.id}selected`"
                                v-bind:name="`${row.item.id}selected`"
                                v-model="row.item.selected"
                                :disabled="row.item.edit"
                            >
                            </b-form-checkbox>
                        </template>
                        <template v-slot:cell(edit)="row">
                            <b-form-checkbox
                                v-bind:id="`${row.item.id}edit`"
                                v-bind:name="`${row.item.id}edit`"
                                v-model="row.item.edit"
                                @input="$_onInputEdit(row.item)"
                            >
                            </b-form-checkbox>
                        </template>
                        <template v-slot:cell(remove)="row">
                            <b-form-checkbox
                                v-bind:id="`${row.item.id}remove`"
                                v-bind:name="`${row.item.id}remove`"
                                v-model="row.item.remove"
                                :disabled="!row.item.edit"
                            >
                            </b-form-checkbox>
                        </template>
                    </b-table>
                </div>
                <div v-if="isProfileApi && hasAnyApiLicense">
                    <!-- Apis -->
                    <h4>{{ $t('apiSettings.title') }}</h4>
                    <b-table
                        striped
                        hover
                        small
                        outlined
                        bordered
                        :tbody-tr-class="$_rowClass"
                        :items="computedApis"
                        :fields="apiFields"
                        :sort-by.sync="sortBy"
                        :sort-desc.sync="sortDesc"
                    >
                        <template v-slot:cell(selected)="row">
                            <div class="d-flex align-items-center">
                                <b-form-checkbox
                                    v-bind:id="`${row.item.apiName}selected`"
                                    v-bind:name="`${row.item.apiName}selected`"
                                    v-model="row.item.selected"
                                    @change="$_onSelectedChange(row.item)"
                                >
                                </b-form-checkbox>
                                <div
                                    v-if="row.item.isStufApi"
                                    class="d-flex align-items-center"
                                >
                                    <label
                                        :for="`${row.item.apiName}stufQuery`"
                                        class="me-1 mb-0 mr-1 ml-3"
                                    >
                                        {{ $t('labelPersonRights') }}
                                    </label>
                                    <b-form-checkbox
                                        class="mr-2"
                                        :id="`${row.item.apiName}stufQuery`"
                                        :name="`${row.item.apiName}stufQuery`"
                                        v-model="
                                            row.item.allowStufPersonQueries
                                        "
                                        :disabled="!row.item.selected"
                                    >
                                    </b-form-checkbox>
                                </div>
                            </div>
                        </template>
                    </b-table>
                </div>
                <!-- Save/cancel/remove buttons -->
                <SubmitCancelRemove
                    :is-saving="isSaving"
                    :is-deleting="isDeleting"
                    :mode="editMode ? modeEditConst : modeAddConst"
                    @cancel="$_onCancel"
                    @remove="$_onRemove"
                    :isBtnSaveDisabled="isBtnSaveDisabled"
                >
                </SubmitCancelRemove>
            </div>
        </b-form>
    </div>
</template>

<script>
    import { nprogress } from '../../services/utilities';
    import { orderBy } from 'lodash';
    import { CONTENT_TYPE_ID, MODE_ADD, MODE_EDIT } from '../../constants';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import xcheckbox from '../../components/BaseComponents/x-checkbox.vue';
    import TextInput from '../../components/BaseComponents/x-input.vue';
    import SubmitCancelRemove from '../../components/SubmitCancelRemove.vue';
    import DualListBox from '../../components/DualListBox.vue';
    import { mapState, mapGetters } from 'vuex';

    export default {
        mixins: [modalMixin],

        components: {
            'x-checkbox': xcheckbox,
            TextInput,
            SubmitCancelRemove,
            DualListBox
        },

        props: {
            id: { type: Number, required: false, default: -1 },
            isProfileBaseLayer: { type: Boolean, required: false, default: false },
            isProfileThemeFeedback: {
                type: Boolean,
                required: false,
                default: false
            },
            isProfileApi: { type: Boolean, required: false, default: false }
        },

        data() {
            return {
                // Constants
                modeAddConst: MODE_ADD,
                modeEditConst: MODE_EDIT,

                userInfo: {},
                record: {},

                isSaving: false,
                isDeleting: false,

                errorInUse: false,
                editMode: false,

                sortBy: 'name',
                sortDesc: false,

                fields: [
                    {
                        key: 'name',
                        label: this.$t('storageLayer'),
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'selected',
                        label: this.$t('labelRead'),
                        thStyle: {
                            border: 0,
                            width: '80px'
                        },
                        tdClass: 'text-center',
                        thClass: 'text-center'
                    },
                    {
                        key: 'edit',
                        label: this.$t('labelUpdate'),
                        thStyle: {
                            border: 0,
                            width: '80px'
                        },
                        tdClass: 'text-center',
                        thClass: 'text-center'
                    },
                    {
                        key: 'remove',
                        label: this.$t('labelRemove'),
                        thStyle: {
                            border: 0,
                            width: '80px'
                        },
                        tdClass: 'text-center',
                        thClass: 'text-center'
                    }
                ],
                apiFields: [
                    {
                        key: 'apiName',
                        label: this.$t('apiSettings.nameTitle'),
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'selected',
                        label: this.$t('apiSettings.authorizedTitle'),
                        thStyle: {
                            border: 0,
                            width: '80px'
                        },
                        tdClass: 'text-center',
                        thClass: 'text-center'
                    }
                ]
            };
        },

        computed: {
            ...mapState(['hasAnyApiLicense']),
            ...mapGetters(['getCheckForFeatureToggle']),
            /**
             * Check if profile can be saved
             * @returns {boolean} indicating if profile can be saved
             */
            isBtnSaveDisabled() {
                return this.isSaving || this.isDeleting || this.errorInUse;
            },

            computedThemesFeedback() {
                if (this.record.feedbackThemes) {
                    return this.record.feedbackThemes.filter(
                        (feedback) =>
                            feedback.contentTypeId === CONTENT_TYPE_ID.FEEDBACK
                    );
                }
                return [];
            },

            computedApis() {
                if (this.record) {
                    return orderBy(this.record.apis, [
                        (api) => api.apiName.toLowerCase()
                    ]);
                }
                return [];
            },

            computedThemesMeasure() {
                if (this.record.feedbackThemes) {
                    return this.record.feedbackThemes.filter(
                        (feedback) =>
                            feedback.contentTypeId ===
                                CONTENT_TYPE_ID.MEASUREMENT &&
                            !feedback.isWebMeasurement
                    );
                }
                return [];
            },

            computedAvailableThemes() {
                if (this.record.themes && this.record.themes.length > 0) {
                    const filteredThemes = this.record.themes.filter((item) => {
                        return item.userEnabled === false;
                    });
                    return orderBy(filteredThemes, [
                        (theme) => theme.name.toLowerCase()
                    ]);
                }
                return [];
            },

            computedAvailableTableSearchResults() {
                if (
                    this.record.tableSearchResults &&
                    this.record.tableSearchResults.length > 0
                ) {
                    const filteredTableSearchResults =
                        this.record.tableSearchResults.filter((item) => {
                            return item.userEnabled === false;
                        });
                    return orderBy(filteredTableSearchResults, [
                        (f) => f.name.toLowerCase()
                    ]);
                }
                return [];
            },

            computedAvailableLayers3D() {
                if (this.record.layers3D && this.record.layers3D.length > 0) {
                    const filteredLayers3D = this.record.layers3D.filter((item) => {
                        return item.userEnabled === false;
                    });
                    return orderBy(filteredLayers3D, [(f) => f.name.toLowerCase()]);
                }
                return [];
            },

            computedAvailableBaseLayers() {
                if (this.record.baseLayers && this.record.baseLayers.length > 0) {
                    const filteredBaseLayers = this.record.baseLayers.filter(
                        (item) => {
                            return item.userEnabled === false;
                        }
                    );
                    return orderBy(filteredBaseLayers, [
                        (baseLayer) => baseLayer.title.toLowerCase()
                    ]);
                }
                return [];
            },

            computedSelectedThemes() {
                if (this.record.themes && this.record.themes.length > 0) {
                    return this.record.themes.filter((item) => {
                        return item.userEnabled === true;
                    });
                }
                return [];
            },

            computedSelectedTableSearchResults() {
                if (
                    this.record.tableSearchResults &&
                    this.record.tableSearchResults.length > 0
                ) {
                    return this.record.tableSearchResults.filter((item) => {
                        return item.userEnabled === true;
                    });
                }
                return [];
            },

            computedSelectedLayers3D() {
                if (this.record.layers3D && this.record.layers3D.length > 0) {
                    return this.record.layers3D.filter((item) => {
                        return item.userEnabled === true;
                    });
                }
                return [];
            },

            computedSelectedBaseLayers() {
                if (this.record.baseLayers && this.record.baseLayers.length > 0) {
                    return this.record.baseLayers.filter((item) => {
                        return item.userEnabled === true;
                    });
                }
                return [];
            },

            computedThemes() {
                return this.record.themes;
            },

            computedTableSearchResults() {
                return this.record.tableSearchResults;
            },

            computedLayers3D() {
                return this.record.layers3D;
            },

            computedBaseLayers() {
                return this.record.baseLayers;
            }
        },

        created() {
            this.userInfo = this.$store.getters.userInfo;

            if (this.id && this.id > 0) {
                this.editMode = true;
                if (this.isProfileThemeFeedback) {
                    this.title = this.$t('profileThemeCardDetailEditTitle');
                }
                if (this.isProfileBaseLayer) {
                    this.title = this.$t('profileBaseCardDetailEditTitle');
                }
                if (this.isProfileApi) {
                    this.title = this.$t('profileApiCardDetailEditTitle');
                }
            } else {
                this.editMode = false;
                if (this.isProfileThemeFeedback) {
                    this.title = this.$t('profileThemeCardDetailAddTitle');
                }
                if (this.isProfileBaseLayer) {
                    this.title = this.$t('profileBaseCardDetailAddTitle');
                }
                if (this.isProfileApi) {
                    this.title = this.$t('profileApiCardDetailAddTitle');
                }
            }
        },

        mounted() {
            if (this.editMode) {
                this.$_getProfile();
            } else {
                // Default settings for new profile
                this.record = {
                    profiletypeid: this.isProfileBaseLayer ? 1 : 2,
                    organization: this.userInfo.organizationId
                };
                this.$_getNewDetails();
            }
        },

        methods: {
            $_rowClass() {
                return 'tablerow';
            },

            /**
             * Update selected boolean, do not switch off 'selected' when 'edit' is switched off
             * @param {object} rowItem - feedback/theme object
             */
            $_onInputEdit(rowItem) {
                if (rowItem.edit) {
                    rowItem.selected = rowItem.edit;
                } else {
                    rowItem.remove = false;
                }
            },

            /**
             * Checks if the given user name already exists in the database
             * @return {void} - Sets error message if needed
             */
            async $_exists() {
                const data = {
                    name: this.record.name,
                    id: this.record.id ? this.record.id : 0
                };
                await this.$store
                    .dispatch('useApiData', {
                        name: 'ProfileExists',
                        data
                    })
                    .then((result) => {
                        this.errorInUse = result.data;
                    });
            },

            async $_getProfile() {
                let url = '';
                if (this.isProfileThemeFeedback) {
                    url = 'GetThemeDetails';
                }

                if (this.isProfileBaseLayer) {
                    url = 'GetBaseLayerDetails';
                }

                if (this.isProfileApi) {
                    url = 'GetApiDetails';
                }
                nprogress.start();
                await this.$store
                    .dispatch('useApiData', {
                        name: url,
                        parameters: { id: this.id }
                    })
                    .then((result) => {
                        // Make object properties reactive for Vue
                        this.record = Object.assign({}, result.data);
                        nprogress.done();
                    });
            },

            async $_getNewDetails() {
                let url = '';
                if (this.isProfileThemeFeedback) {
                    url = 'GetNewThemeDetails';
                }

                if (this.isProfileBaseLayer) {
                    url = 'GetNewBaseLayerDetails';
                }

                if (this.isProfileApi) {
                    url = 'GetNewApiDetails';
                }
                // Get existing data from the backend
                await this.$store
                    .dispatch('useApiData', {
                        name: url
                    })
                    .then((result) => {
                        this.record = Object.assign({}, result.data);
                    });
            },

            $_sortRecords(records) {
                const enabledList = [];
                const disabledList = [];

                if (records && records.length > 1) {
                    for (let record of records) {
                        if (record.userEnabled) {
                            enabledList.push(record);
                        } else {
                            disabledList.push(record);
                        }
                    }
                    records = enabledList.concat(disabledList);

                    for (let j = 0; j < records.length; j++) {
                        records[j].order = j;
                    }
                }
            },

            $_gotoOverview() {
                this.$router.push({ path: '/dashboard/profiles' });
            },

            $_onSubmit() {
                if (this.$refs.profileform.checkValidity()) {
                    if (this.isProfileThemeFeedback) {
                        this.$_onSave('SaveProfileThemes');
                    }
                    if (this.isProfileBaseLayer) {
                        this.$_onSave('SaveBaseLayers');
                    }
                    if (this.isProfileApi) {
                        this.$_onSave('SaveProfileApis');
                    }
                }
            },

            async $_onSave(action) {
                // Sort records first
                if (this.isProfileThemeFeedback) {
                    this.$_sortRecords(this.record.themes);
                }
                if (this.isProfileBaseLayer) {
                    this.$_sortRecords(this.record.baseLayers);
                }

                this.record.orgChanged = false;
                this.isSaving = true;
                nprogress.start();
                /**
                 * Save profile
                 */
                await this.$store
                    .dispatch('useApiData', {
                        name: action,
                        data: this.record
                    })
                    .then((result) => {
                        const data = {
                            message: this.$t('profileCardSavedConfirmation'),
                            variant: 'success',
                            autoHideDelay: 2500,
                            noCloseButton: true
                        };

                        this.showToast(data);
                        this.isSaving = false;

                        if (!this.editMode) {
                            this.$_gotoOverview();
                        }
                        nprogress.done();
                    })
                    .catch((error) => {
                        nprogress.done();
                        const data = {
                            message: error.message,
                            variant: 'danger',
                            title: this.$t('saveFailureTitle'),
                            autoHideDelay: 10000
                        };
                        this.showToast(data);
                        this.isSaving = false;
                    });
            },

            $_onRemove() {
                const confirmMessage = {
                    message: this.$t('profileCardDeleteConfirmation')
                };
                this.msgBoxConfirm(confirmMessage).then(async (confirm) => {
                    if (confirm) {
                        this.isDeleting = true;

                        await this.$store
                            .dispatch('useApiData', {
                                name: 'DeleteProfile',
                                parameters: { id: this.record.id }
                            })
                            .then(() => {
                                this.$_gotoOverview();
                                this.isDeleting = false;
                            })
                            .catch((error) => {
                                const data = {
                                    message: this.$t('deleteFailureMessage', [
                                        `${error.message}`
                                    ]),
                                    variant: 'danger',
                                    title: this.$t('deleteFailureTitle'),
                                    autoHideDelay: 10000
                                };
                                this.showToast(data);
                                this.isDeleting = false;
                            });
                    }
                });
            },

            $_onCancel() {
                this.$_gotoOverview();
            },

            $_updateComputedThemes(newValue) {
                this.record.themes = newValue;
            },
            $_updateComputedTableSearchResults(newValue) {
                this.record.tableSearchResults = newValue;
            },
            $_updateComputedLayers3D(newValue) {
                this.record.layers3D = newValue;
            },
            $_updateComputedBaseLayers(newValue) {
                this.record.baseLayers = newValue;
            },
            $_onSelectedChange(item) {
                if (!item.selected) {
                    item.allowStufPersonQueries = false;
                }
            }
        }
    };
</script>
